<template>
  <div class="facility-explorer-page">
    <div class="fx-shell">
      <header class="console-page-head">
        <div class="console-page-copy">
          <h2 class="console-title">{{ pageTitleOverride || '시설검색' }}</h2>
        </div>
        <div class="console-head-side">
          <span class="console-count">{{ pagination.total.toLocaleString() }}건</span>
          <v-btn variant="flat" class="fx-excel-btn" size="small" @click="excelDialogOpen = true">
            <v-icon size="16">mdi-microsoft-excel</v-icon>
            엑셀로 내려받기
          </v-btn>
        </div>
      </header>

      <section class="fx-filters">
        <div class="fx-filter-row">
          <div class="fx-field">
            <label class="fx-label">시·도</label>
            <select v-model="filters.sidoCode" class="fx-select" @change="onSidoChange">
              <option :value="null">전체</option>
              <option v-for="s in sidoOptions" :key="s.sido_code" :value="s.sido_code">
                {{ s.sido_name }}
              </option>
            </select>
          </div>
          <div class="fx-field">
            <label class="fx-label">시·군·구</label>
            <select v-model="filters.sigunguCode" class="fx-select" :disabled="!filters.sidoCode">
              <option :value="null">전체</option>
              <option v-for="g in sigunguOptions" :key="g.sigungu_code" :value="g.sigungu_code">
                {{ g.sigungu_name }}
              </option>
            </select>
          </div>
          <div class="fx-field">
            <label class="fx-label">영업상태</label>
            <select v-model="filters.salesFilter" class="fx-select">
              <option :value="null">전체</option>
              <option v-for="opt in salesFilterOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
          </div>
          <div class="fx-field">
            <label class="fx-label">임금진단</label>
            <select v-model="filters.wageDiagFilter" class="fx-select">
              <option v-for="opt in wageDiagFilterOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
          </div>
          <div class="fx-field fx-field--grow">
            <label class="fx-label">시설명 검색</label>
            <div class="fx-search-wrap">
              <v-icon size="16" class="fx-search-icon">mdi-magnify</v-icon>
              <input
                v-model="filters.search"
                type="text"
                class="fx-input"
                placeholder="시설명 / 주소 / 번호"
                @keyup.enter="fetchList(1)"
              />
              <button
                v-if="filters.search"
                type="button"
                class="fx-search-clear"
                @click="filters.search = ''"
              >
                <v-icon size="12">mdi-close</v-icon>
              </button>
            </div>
          </div>
          <div class="fx-actions">
            <v-btn variant="flat" class="btn-search" @click="fetchList(1)">
              <v-icon size="16" class="mr-1">mdi-magnify</v-icon>
              검색
            </v-btn>
            <v-btn variant="flat" class="btn-reset" @click="resetFilters">
              초기화
            </v-btn>
          </div>
        </div>

        <div class="fx-biz-row">
          <span class="fx-label">제공서비스</span>
          <div class="fx-biz-chips">
            <button
              v-for="opt in bizGubunSelectOptions"
              :key="opt.value"
              type="button"
              class="fx-biz-chip"
              :class="{ 'fx-biz-chip--selected': filters.bizGubuns.includes(opt.value) }"
              :style="{ '--chip-color': opt.color }"
              @click="toggleBizGubun(opt.value)"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>
      </section>

      <main class="fx-main">
        <div class="fx-table-wrap">
          <LoadingOverlay :show="loading" text="불러오는 중..." />
          <v-data-table
            class="facility-explorer-data-table"
            :headers="tableHeaders"
            :items="rows"
            :loading="loading"
            :items-per-page="pagination.limit"
            fixed-header
            hover
          >
            <template v-slot:item.index="{ index }">
              <span class="row-num">{{ (pagination.page - 1) * pagination.limit + index + 1 }}</span>
            </template>

            <template v-slot:item.cc_name="{ item }">
              <button type="button" class="facility-name-link" @click.stop="openDetail(item.cc_number)">
                <span class="facility-name">{{ item.cc_name }}</span>
                <span v-if="regionText(item)" class="facility-region">{{ regionText(item) }}</span>
              </button>
            </template>

            <template v-slot:item.cc_biz_gubun="{ item }">
              <div v-if="item.cc_biz_gubun" class="biz-chips">
                <v-chip
                  v-for="(chip, ci) in parseBizGubun(item.cc_biz_gubun)"
                  :key="ci"
                  :color="chip.color"
                  size="x-small"
                  label
                  variant="flat"
                  class="biz-chip"
                  :title="chip.short"
                >
                  {{ chip.mark }}
                </v-chip>
              </div>
              <span v-else class="text-grey">-</span>
            </template>

            <template v-slot:item.capacity="{ item }">
              <span class="facility-num-cell">{{ item.total_capacity || 0 }} / {{ item.total_current || 0 }}</span>
            </template>

            <template v-slot:item.sales_status="{ item }">
              <template v-if="item.sales_idx && item.sales_status === '영업중'">
                <v-chip size="x-small" variant="flat" color="#1565c0">
                  <v-icon size="10" class="mr-1">mdi-briefcase-outline</v-icon>
                  영업중
                </v-chip>
                <template v-if="canSeeSalesDetail(item.sales_org_code)">
                  <span class="sales-info-text">{{ item.sales_org_name }}</span>
                  <span v-if="item.sales_user_name" class="sales-info-text">· {{ item.sales_user_name }}</span>
                </template>
              </template>
              <template v-else-if="item.sales_idx && item.sales_status === '회원가입'">
                <v-chip size="x-small" variant="flat" color="#2e7d32">
                  <v-icon size="10" class="mr-1">mdi-check-circle-outline</v-icon>
                  회원가입성공
                </v-chip>
                <template v-if="canSeeSalesDetail(item.sales_org_code)">
                  <span class="sales-info-text">{{ item.sales_org_name }}</span>
                  <span v-if="item.sales_user_name" class="sales-info-text">· {{ item.sales_user_name }}</span>
                </template>
              </template>
              <span v-else class="text-grey">-</span>
            </template>

            <template v-slot:item.sales_log_count="{ item }">
              <span v-if="item.sales_log_count > 0" class="sales-log-count">{{ item.sales_log_count }}건</span>
              <span v-else class="text-grey">-</span>
            </template>

            <template v-slot:item.wage_diag="{ item }">
              <v-btn
                variant="flat"
                :class="item.wage_diag_count > 0 ? 'wage-diag-cell-btn--done' : 'wage-diag-cell-btn--pending'"
                class="wage-diag-cell-btn"
                @click.stop="openWageDiag(item)"
              >
                <v-icon size="12" class="mr-1">{{ item.wage_diag_count > 0 ? 'mdi-check-circle' : 'mdi-file-chart' }}</v-icon>
                {{ item.wage_diag_count > 0 ? '완료' : '진단' }}
              </v-btn>
            </template>

            <template v-slot:no-data>
              <div class="empty-message">
                <v-icon size="48" color="grey-lighten-1">mdi-database-off-outline</v-icon>
                <p>검색 결과가 없습니다</p>
              </div>
            </template>

            <template v-slot:bottom>
              <div class="table-footer">
                <div class="footer-left">
                  <span class="total-count">총 <strong>{{ pagination.total.toLocaleString() }}</strong>건</span>
                </div>
                <div class="footer-center">
                  <v-pagination
                    v-if="pagination.totalPages > 1"
                    v-model="pagination.page"
                    :length="pagination.totalPages"
                    :total-visible="7"
                    density="compact"
                    @update:model-value="fetchList"
                  />
                </div>
                <div class="footer-right">
                  <v-select
                    v-model="pagination.limit"
                    :items="[50, 100, 200, 300]"
                    density="compact"
                    variant="outlined"
                    hide-details
                    class="page-size-select"
                    @update:model-value="fetchList(1)"
                  >
                    <template v-slot:selection="{ item }">{{ item.value }}건씩</template>
                  </v-select>
                </div>
              </div>
            </template>
          </v-data-table>
        </div>
      </main>
    </div>

    <WageSimpleDiagnosisDialog
      v-model="wageDiagOpen"
      :facility="wageDiagTarget"
      @saved="onWageDiagSaved"
      @deleted="onWageDiagDeleted"
    />

    <CrawlExcelDownloadDialog
      v-model="excelDialogOpen"
      :initial-filters="excelSeedFilters"
    />
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { crawlApi, salesApi, orgApi } from '@/services/api';
import CrawlExcelDownloadDialog from '@/components/crawl/CrawlExcelDownloadDialog.vue';
import WageSimpleDiagnosisDialog from '@/components/facility/WageSimpleDiagnosisDialog.vue';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';

const store = useStore();
const route = useRoute();
const currentUser = computed(() => store.getters.currentUser);

const loading = ref(false);
const excelDialogOpen = ref(false);
const wageDiagOpen = ref(false);
const wageDiagTarget = ref<any>(null);
const rows = ref<any[]>([]);
const sidoOptions = ref<any[]>([]);
const sigunguOptions = ref<any[]>([]);

const visibleOrgCodes = ref<Set<string>>(new Set());
const bizGubunMap = ref<any[]>([]);

const bizGubunSelectOptions = computed(() => {
  return bizGubunMap.value.map((m: any) => ({
    label: m.cbm_short,
    value: m.cbm_short,
    color: m.cbm_color
  }));
});

const toggleBizGubun = (value: string) => {
  const idx = filters.bizGubuns.indexOf(value);
  if (idx >= 0) {
    filters.bizGubuns.splice(idx, 1);
  } else {
    filters.bizGubuns.push(value);
  }
};

const bizTableMark = (short: string) => {
  const marks: Record<string, string> = {
    요양시설: '요양',
    주야간보호: '주야',
    방문요양: '방문',
    방문목욕: '목욕',
    방문간호: '간호',
    공동생활가정: '공동',
    복지용구: '복지',
    치매전담1실: '치1',
    치매전담2실: '치2',
    주야간치매전담1실: '주1',
  };
  return marks[short] || short.slice(0, 2);
};

const parseBizGubun = (raw: string) => {
  if (!raw) return [];
  const names = raw.split(',').map(s => s.trim());
  const seen = new Set<string>();
  const result: { short: string; mark: string; color: string }[] = [];

  for (const name of names) {
    const found = bizGubunMap.value.find((m: any) => {
      return m.cbm_raw.split(',').map((s: string) => s.trim()).includes(name);
    });
    const short = found ? found.cbm_short : name;
    const color = found ? found.cbm_color : '#90a4ae';
    if (!seen.has(short)) {
      seen.add(short);
      result.push({ short, mark: bizTableMark(short), color });
    }
  }
  return result;
};

const getBizShortText = (raw: string) => {
  const chips = parseBizGubun(raw);
  return chips.map((c) => c.short).join(', ');
};

const openWageDiag = (item: any) => {
  wageDiagTarget.value = {
    ccNumber: item.cc_number,
    name: item.cc_name,
    biz: getBizShortText(item.cc_biz_gubun),
    wageDiagCount: item.wage_diag_count || 0,
    wageDiagWdate: item.wage_diag_wdate,
    wageDiagUId: item.wage_diag_u_id,
  };
  wageDiagOpen.value = true;
};

const syncWageDiagTarget = (ccNumber: string) => {
  const row = rows.value.find((r: any) => r.cc_number === ccNumber);
  if (row && wageDiagTarget.value?.ccNumber === ccNumber) {
    wageDiagTarget.value = {
      ...wageDiagTarget.value,
      wageDiagCount: row.wage_diag_count || 0,
      wageDiagWdate: row.wage_diag_wdate,
      wageDiagUId: row.wage_diag_u_id,
    };
  }
};

const onWageDiagSaved = async (ccNumber: string) => {
  await fetchList(pagination.page);
  syncWageDiagTarget(ccNumber);
};

const onWageDiagDeleted = async (ccNumber: string) => {
  await fetchList(pagination.page);
  syncWageDiagTarget(ccNumber);
};

const filters = reactive({
  sidoCode: null as string | null,
  sigunguCode: null as string | null,
  bizGubuns: [] as string[],
  minCapacity: null as number | null,
  minCurrent: null as number | null,
  search: '',
  salesFilter: null as string | null,
  wageDiagFilter: '' as string,
});

const salesFilterOptions = [
  { label: '영업중', value: 'active' },
  { label: '회원가입성공', value: 'completed' },
  { label: '미영업', value: 'none' }
];

const wageDiagFilterOptions = [
  { label: '전체', value: '' },
  { label: '임금진단 완료', value: 'done' },
  { label: '미진단', value: 'none' },
];

const excelSeedFilters = computed(() => {
  const hintParts: string[] = [];

  if (filters.sidoCode) {
    const sido = sidoOptions.value.find((s: any) => s.sido_code === filters.sidoCode);
    if (sido) hintParts.push(sido.sido_name);
  }
  if (filters.sigunguCode) {
    const sigungu = sigunguOptions.value.find((s: any) => s.sigungu_code === filters.sigunguCode);
    if (sigungu) hintParts.push(sigungu.sigungu_name);
  }
  if (filters.bizGubuns.length > 0) {
    hintParts.push(filters.bizGubuns.join(', '));
  }
  if (filters.salesFilter) {
    const sales = salesFilterOptions.find(o => o.value === filters.salesFilter);
    if (sales) hintParts.push(sales.label);
  }
  if (filters.wageDiagFilter) {
    const wage = wageDiagFilterOptions.find(o => o.value === filters.wageDiagFilter);
    if (wage && wage.value) hintParts.push(wage.label);
  }
  const searchText = filters.search?.trim();
  if (searchText) {
    hintParts.push(`검색:${searchText}`);
  }
  if (filters.minCapacity != null) {
    hintParts.push(`정원≥${filters.minCapacity}`);
  }
  if (filters.minCurrent != null) {
    hintParts.push(`현원≥${filters.minCurrent}`);
  }

  return {
    sidoCodes: filters.sidoCode ? [filters.sidoCode] : [],
    sigunguCode: filters.sigunguCode,
    bizGubuns: filters.bizGubuns.length ? [...filters.bizGubuns] : [],
    search: searchText || undefined,
    salesFilter: filters.salesFilter || undefined,
    total_capacity_min: filters.minCapacity ?? undefined,
    total_current_min: filters.minCurrent ?? undefined,
    filterHint: hintParts.length ? hintParts.join(' · ') : undefined,
  };
});

const pageTitleOverride = ref('');

const applyRouteQueryFilters = async () => {
  const q = route.query;
  const qSido = typeof q.sido === 'string' ? q.sido : null;
  const qSigungu = typeof q.sigungu === 'string' ? q.sigungu : null;
  const qBiz = typeof q.bizGubun === 'string' ? q.bizGubun : null;
  const qLabel = typeof q.label === 'string' ? q.label : null;
  const qSearch = typeof q.search === 'string' ? q.search.trim() : '';

  if (qLabel) pageTitleOverride.value = qLabel;
  if (qSearch) filters.search = qSearch;

  if (!qSido && !qSigungu && !qBiz) return;

  if (qSido) {
    filters.sidoCode = qSido;
    await onSidoChange();
  }
  if (qSigungu) {
    filters.sigunguCode = qSigungu;
  }
  if (qBiz) {
    filters.bizGubuns = [qBiz];
  }
};

const pagination = reactive({
  page: 1,
  limit: 100,
  total: 0,
  totalPages: 1
});

const regionText = (item: { sido_name?: string; sigungu_name?: string }) =>
  [item.sido_name, item.sigungu_name].filter(Boolean).join(' ');

const tableHeaders = [
  { title: 'No', key: 'index', sortable: false, align: 'center' as const },
  { title: '시설명', key: 'cc_name', sortable: true },
  { title: '제공서비스', key: 'cc_biz_gubun', sortable: false },
  { title: '정원/현원', key: 'capacity', sortable: false, align: 'end' as const },
  { title: '영업', key: 'sales_status', sortable: false },
  { title: '이력', key: 'sales_log_count', sortable: true, align: 'center' as const },
  { title: '임금진단', key: 'wage_diag', sortable: false, align: 'center' as const },
];

const canSeeSalesDetail = (salesOrgCode: string) => {
  if (!currentUser.value) return false;
  if (currentUser.value.roleLevel >= 60) return true;
  if (currentUser.value.roleLevel >= 60) {
    return visibleOrgCodes.value.has(salesOrgCode);
  }
  return false;
};

const collectDescendantCodes = (tree: any[], orgCode: string): string[] => {
  const codes: string[] = [];
  const findAndCollect = (nodes: any[], found: boolean) => {
    for (const node of nodes) {
      if (found || node.org_code === orgCode) {
        codes.push(node.org_code);
        if (node.children) {
          findAndCollect(node.children, true);
        }
        if (!found && node.org_code === orgCode) continue;
      }
      if (!found && node.children) {
        findAndCollect(node.children, false);
      }
    }
  };
  findAndCollect(tree, false);
  return codes;
};

const loadVisibleOrgCodes = async () => {
  if (!currentUser.value || currentUser.value.roleLevel < 60) return;
  if (currentUser.value.roleLevel >= 60) return;

  try {
    const res = await orgApi.getTree();
    const tree = res.data?.data || [];
    const myOrgCode = currentUser.value.orgCode;
    if (myOrgCode) {
      visibleOrgCodes.value = new Set(collectDescendantCodes(tree, myOrgCode));
    }
  } catch (error) {
    console.error('조직 트리 로드 오류:', error);
  }
};

let detailPopupWindow: Window | null = null;

const onSidoChange = async () => {
  filters.sigunguCode = null;
  if (!filters.sidoCode) {
    sigunguOptions.value = [];
    return;
  }
  try {
    const res = await crawlApi.getSigunguList(filters.sidoCode);
    sigunguOptions.value = res.data?.data || [];
  } catch (error: any) {
    if (error?.response?.status === 401) return;
    sigunguOptions.value = [];
  }
};

const fetchOptions = async () => {
  try {
    const [sidoRes, bizRes] = await Promise.all([
      crawlApi.getSidoList(),
      crawlApi.getBizGubunList()
    ]);
    sidoOptions.value = sidoRes.data?.data || [];
    bizGubunMap.value = bizRes.data?.data || [];
  } catch (error: any) {
    if (error?.response?.status === 401) return;
    console.error('시설검색 옵션 로드 오류:', error);
  }
};

const fetchList = async (page = pagination.page) => {
  loading.value = true;
  try {
    pagination.page = page;
    const res = await crawlApi.getList({
      sidoCode: filters.sidoCode,
      sigunguCode: filters.sigunguCode,
      bizGubuns: filters.bizGubuns.length ? filters.bizGubuns.join(',') : null,
      search: filters.search || undefined,
      minCapacity: filters.minCapacity,
      minCurrent: filters.minCurrent,
      salesFilter: filters.salesFilter,
      wageDiagFilter: filters.wageDiagFilter || null,
      page: pagination.page,
      limit: pagination.limit,
    });
    rows.value = res.data?.data || [];
    pagination.total = res.data?.pagination?.total || 0;
    pagination.totalPages = res.data?.pagination?.totalPages || 1;
  } catch (error: any) {
    // 401은 api 인터셉터가 로그인으로 보냄 — 오버레이만 막음
    if (error?.response?.status === 401) return;
    rows.value = [];
    pagination.total = 0;
    pagination.totalPages = 1;
    const message = error?.response?.data?.message || error?.message || '알 수 없는 오류';
    alert(`시설 목록 조회 실패: ${message}`);
    console.error('시설 목록 조회 오류:', error);
  } finally {
    loading.value = false;
  }
};

const resetFilters = async () => {
  filters.sidoCode = null;
  filters.sigunguCode = null;
  filters.bizGubuns = [];
  filters.minCapacity = null;
  filters.minCurrent = null;
  filters.search = '';
  filters.salesFilter = null;
  filters.wageDiagFilter = '';
  sigunguOptions.value = [];
  await fetchList(1);
};

const openDetail = (ccNumber: string) => {
  const url = `/facility/detail/${ccNumber}`;
  const width = 1100;
  const height = 720;
  const left = 0;
  const top = 0;
  const features = `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=yes,status=no,resizable=yes,scrollbars=yes`;

  detailPopupWindow = window.open(url, 'facilityDetailPopup', features);

  if (detailPopupWindow) {
    detailPopupWindow.focus();
  }
};

const handlePopupMessage = async (event: MessageEvent) => {
  if (event.data?.type !== 'salesUpdated') return;
  const ccNumber = event.data.ccNumber;
  if (!ccNumber) return;

  try {
    const res = await salesApi.getSalesByFacility(ccNumber);
    const sales = res.data?.data?.sales || null;
    const logs = res.data?.data?.logs || [];

    const rowIndex = rows.value.findIndex((r: any) => r.cc_number === ccNumber);
    if (rowIndex !== -1) {
      if (sales) {
        rows.value[rowIndex].sales_idx = sales.sales_idx;
        rows.value[rowIndex].sales_status = sales.sales_status;
        rows.value[rowIndex].sales_u_idx = sales.u_idx;
        rows.value[rowIndex].sales_org_code = sales.org_code;
        rows.value[rowIndex].sales_org_name = sales.sales_org_name;
        rows.value[rowIndex].sales_user_name = sales.sales_user_name;
      } else {
        rows.value[rowIndex].sales_idx = null;
        rows.value[rowIndex].sales_status = null;
        rows.value[rowIndex].sales_u_idx = null;
        rows.value[rowIndex].sales_org_code = null;
        rows.value[rowIndex].sales_org_name = null;
        rows.value[rowIndex].sales_user_name = null;
      }
      rows.value[rowIndex].sales_log_count = logs.length;
    }
  } catch (error) {
    console.error('영업 정보 동기화 오류:', error);
  }
};

onMounted(async () => {
  window.addEventListener('message', handlePopupMessage);
  try {
    await Promise.all([
      fetchOptions(),
      loadVisibleOrgCodes()
    ]);
    await applyRouteQueryFilters();
    await fetchList(1);
  } catch (error: any) {
    if (error?.response?.status === 401) return;
    console.error('시설검색 초기화 오류:', error);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('message', handlePopupMessage);
});
</script>
